import { useState, useRef, useEffect } from "react";
import LocalLink from "@/components/LocalLink";
import { ChevronDown, BookOpen, GraduationCap } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const links = [
  { icon: BookOpen, labelKey: "nav.resources.insights", href: "/insights" },
  { icon: GraduationCap, labelKey: "nav.resources.quiz", href: "/citizenship-test" },
];

const ResourcesDropdown = ({ mobile = false, onNavigate }: { mobile?: boolean; onNavigate?: () => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMouseEnter = () => {
    if (mobile) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (mobile) return;
    timeoutRef.current = setTimeout(() => setIsOpen(false), 250);
  };

  // ─── MOBILE: Accordion ───
  if (mobile) {
    return (
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between w-full min-h-[48px] py-2 pl-2 text-base text-white font-medium hover:text-gold transition-colors"
          aria-expanded={isOpen}
        >
          {t("nav.resources")}
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </button>
        {isOpen && (
          <div className="mt-2 mb-3 ml-2 space-y-0.5 rounded-lg bg-white/[0.03] p-2 motion-safe:animate-fade-in">
            {links.map((link) => {
              const Icon = link.icon;
              return (
                <LocalLink
                  key={link.href}
                  to={link.href}
                  onClick={() => { setIsOpen(false); onNavigate?.(); }}
                  className="flex items-center gap-2 min-h-[44px] py-2 px-2 rounded-md text-sm text-white/70 hover:bg-white/5 hover:text-gold transition-colors border-b border-white/5 last:border-b-0"
                >
                  <Icon className="w-4 h-4 text-gold" />
                  {t(link.labelKey)}
                </LocalLink>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ─── DESKTOP ───
  return (
    <div
      ref={dropdownRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className="flex items-center gap-1 text-sm font-medium text-white/80 hover:text-gold transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        {t("nav.resources")}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-64 bg-navy/95 backdrop-blur-2xl rounded-xl shadow-lg border border-white/10 overflow-hidden animate-fade-in z-50">
          <div className="h-[2px] bg-gradient-to-r from-transparent via-gold to-transparent" />
          <div className="p-2">
            {links.map((link) => {
              const Icon = link.icon;
              return (
                <LocalLink
                  key={link.href}
                  to={link.href}
                  onClick={() => { setIsOpen(false); onNavigate?.(); }}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] text-white/75 hover:bg-white/5 hover:text-gold transition-colors"
                >
                  <span className="w-7 h-7 rounded-md bg-gold/10 flex items-center justify-center shrink-0">
                    <Icon className="w-3.5 h-3.5 text-gold" />
                  </span>
                  {t(link.labelKey)}
                </LocalLink>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourcesDropdown;
